import { getStorage, setStorage } from "@helpers/storage";
import Echo from "laravel-echo";
import { LabHubApi } from "@helpers/api";
import { defineStore } from "pinia";
import { watch } from "vue";
import { useUserStore } from "./userStore";

export const useAppStore = defineStore("app", {
    actions: {
        setThemePreference(theme) {
            this.themePreference = theme;
            this.updateTheme(theme, true);
        },
        async setup(env) {
            // Set env
            this.env = env;

            // Set theme
            this.setThemePreference(getStorage("themePreference") ?? "auto");

            // Watch theme changes
            const that = this;
            watch(
                () => this.themePreference,
                (newTheme) => {
                    that.updateTheme(newTheme, true);
                },
            );

            // Watch window theme changes
            window
                .matchMedia("(prefers-color-scheme: dark)")
                .addEventListener("change", function (e) {
                    that.updateTheme(e.matches ? "dark" : "light");
                });

            // Check if LabHub is enabled
            const userStore = useUserStore();
            if (
                this.env.broadcast.labhub.host &&
                (await userStore.getActiveUserType) === "employee"
            ) {
                this.enableConnection("labhub");
            }
        },
        async enableConnection(connectionName) {
            if (!this.connections[connectionName]) {
                console.error("Connection not found: " + connectionName);
                return;
            }

            this.connections[connectionName].enabled = true;

            let token = "";
            if (connectionName === "labhub") {
                // Make a api request to the login endpoint
                const userStore = useUserStore();
                LabHubApi.post("/login", { token: userStore.getToken("yhub") }).then((response) => {
                    token = response.data.data.token;
                    userStore.setToken("labhub", response.data.data.token);
                });
            }

            // Setup the connection
            this.setupBroadcastConnection(connectionName, token);
        },
        setupBroadcastConnection(connectionName, token) {
            const conconfig = this.connections[connectionName].config;

            // Setup the echo config
            const config = {
                auth: {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                },
                authEndpoint: conconfig.authEndpoint(this),
                broadcaster: "reverb",
                enabledTransports: ["ws", "wss"],
                forceTLS: false,
                key: conconfig.serverConfig(this).key,
                wsHost: conconfig.serverConfig(this).wsHost,
                wsPort: conconfig.serverConfig(this).wsPort,
                wssPort: conconfig.serverConfig(this).wssPort,
            };

            this.connections[connectionName].echo = new Echo(config);

            const connectionEvents = [
                "connected",
                "disconnected",
                "connecting",
                "unavailable",
                "failed",
            ];

            const updateStatus = (status) => {
                this.connections[connectionName].status = status;
            };

            connectionEvents.forEach((event) => {
                this.connections[connectionName].echo.connector.pusher.connection.bind(
                    event,
                    () => {
                        updateStatus(event);
                    },
                );
            });
        },
        updateTheme(theme, updatePreference = false) {
            if (updatePreference) {
                setStorage("themePreference", theme);
            }

            // Check if theme is auto, if so use system theme
            if (theme === "auto") {
                theme = window.matchMedia("(prefers-color-scheme: dark)").matches
                    ? "dark"
                    : "light";
            }

            // Update theme
            this.theme = theme;

            // Update app
            document.documentElement.setAttribute("data-bs-theme", theme);

            // Default colors
            const color = theme === "dark" ? "#16181b" : "#ededed";

            // Set PWA theme
            document.querySelector('meta[name="theme-color"]').setAttribute("content", color);
        },
        isConnectionActive(name) {
            return this.connections[name].status === "connected";
        },
    },
    getters: {
        getTheme() {
            return this.theme;
        },
        getThemePreference() {
            return this.themePreference;
        },
        getActiveConnections() {
            return Object.values(this.connections).filter((connection) => connection.enabled);
        },
    },
    state: () => ({
        env: {},
        theme: null,
        themePreference: null,
        connections: {
            labhub: {
                id: "labhub",
                enabled: false,
                config: {
                    label: "LabHub",
                    authEndpoint: (appStore) => appStore.env.labhub_url + "/api/broadcasting/auth",
                    serverConfig(appStore) {
                        return {
                            key: appStore.env.broadcast.labhub.key,
                            wsHost: appStore.env.broadcast.labhub.host,
                            wsPort: appStore.env.broadcast.labhub.port,
                            wssPort: appStore.env.broadcast.labhub.port,
                        };
                    },
                },
                echo: null,
                status: "disconnected",
            },
            displayLabhub: {
                id: "displayLabhub",
                enabled: false,
                config: {
                    label: "Display LabHub",
                    authEndpoint: (appStore) => appStore.env.labhub_url + "/api/broadcasting/auth",
                    serverConfig(appStore) {
                        return {
                            key: appStore.env.broadcast.labhub.key,
                            wsHost: appStore.env.broadcast.labhub.host,
                            wsPort: appStore.env.broadcast.labhub.port,
                            wssPort: appStore.env.broadcast.labhub.port,
                        };
                    },
                },
                echo: null,
                status: "disconnected",
            },
            displayYhub: {
                id: "displayYhub",
                enabled: false,
                config: {
                    label: "Display Yhub",
                    authEndpoint: (appStore) => "bla",
                    serverConfig(appStore) {
                        return {
                            key: appStore.env.broadcast.yhub.key,
                            wsHost: appStore.env.broadcast.yhub.host,
                            wsPort: appStore.env.broadcast.yhub.port,
                            wssPort: appStore.env.broadcast.yhub.port,
                        };
                    },
                },
                echo: null,
                status: "disconnected",
            },
        },
    }),
});
